// components/DocumentsForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, PenTool, Check, X } from 'lucide-react';

interface DocumentsFormProps {
    selfie: string | null;
    identityDocument: string | null;
    signature: string | null;
    onSelfieChange: (data: string | null) => void;
    onDocumentChange: (data: string | null) => void;
    onSignatureChange: (data: string | null) => void;
    onNext: () => void;
    onPrevious: () => void;
}

const DocumentsForm: React.FC<DocumentsFormProps> = ({
                                                         selfie,
                                                         identityDocument,
                                                         signature,
                                                         onSelfieChange,
                                                         onDocumentChange,
                                                         onSignatureChange,
                                                         onNext,
                                                         onPrevious
                                                     }) => {
    const [isSignaturePadOpen, setIsSignaturePadOpen] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const identityFileInputRef = useRef<HTMLInputElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const signatureCanvasRef = useRef<HTMLCanvasElement>(null);

    // Bloquer le scroll de la page quand le modal de signature est ouvert
    useEffect(() => {
        if (isSignaturePadOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }

        // Cleanup au démontage du composant
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [isSignaturePadOpen]);

    // Gestion camera
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
        } catch (error) {
            alert('Impossible d\'accéder à la caméra');
        }
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const canvas = canvasRef.current;
            const video = videoRef.current;
            const context = canvas.getContext('2d');

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            if (context) {
                context.drawImage(video, 0, 0);
                const imageData = canvas.toDataURL('image/jpeg');
                onSelfieChange(imageData);

                // Arrêter la caméra
                const stream = video.srcObject as MediaStream;
                stream.getTracks().forEach(track => track.stop());
            }
        }
    };

    // Gestion upload
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, type: 'selfie' | 'document') => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const result = e.target?.result as string;
                if (type === 'selfie') {
                    onSelfieChange(result);
                } else {
                    onDocumentChange(result);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    // Gestion signature avec coordonnées pour mobile et desktop
    const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        let clientX, clientY;

        if ('touches' in e) {
            // Événement tactile
            const touch = e.touches[0] || e.changedTouches[0];
            clientX = touch.clientX;
            clientY = touch.clientY;
        } else {
            // Événement souris
            clientX = e.clientX;
            clientY = e.clientY;
        }

        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault(); // Empêcher le comportement par défaut
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;

        setIsDrawing(true);
        const coords = getCoordinates(e);

        const context = canvas.getContext('2d');
        if (context) {
            context.beginPath();
            context.moveTo(coords.x, coords.y);
        }
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault(); // Empêcher le comportement par défaut
        if (!isDrawing) return;

        const canvas = signatureCanvasRef.current;
        if (!canvas) return;

        const coords = getCoordinates(e);

        const context = canvas.getContext('2d');
        if (context) {
            context.lineTo(coords.x, coords.y);
            context.stroke();
        }
    };

    const stopDrawing = (e: React.TouchEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>) => {
        e.preventDefault(); // Empêcher le comportement par défaut
        setIsDrawing(false);
    };

    // Gestion signature
    const initSignaturePad = () => {
        setIsSignaturePadOpen(true);
        setTimeout(() => {
            const canvas = signatureCanvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                canvas.width = rect.width;
                canvas.height = rect.height;

                const context = canvas.getContext('2d');
                if (context) {
                    context.fillStyle = 'white';
                    context.fillRect(0, 0, canvas.width, canvas.height);
                    context.strokeStyle = 'black';
                    context.lineWidth = 2;
                    context.lineCap = 'round';
                }
            }
        }, 100);
    };

    const saveSignature = () => {
        const canvas = signatureCanvasRef.current;
        if (canvas) {
            const imageData = canvas.toDataURL('image/png');
            onSignatureChange(imageData);
            setIsSignaturePadOpen(false);
        }
    };

    const clearSignature = () => {
        const canvas = signatureCanvasRef.current;
        if (canvas) {
            const context = canvas.getContext('2d');
            if (context) {
                context.fillStyle = 'white';
                context.fillRect(0, 0, canvas.width, canvas.height);
            }
        }
    };

    const handleNext = () => {
        if (!selfie) {
            alert('Veuillez ajouter une photo de profil');
            return;
        }
        if (!signature) {
            alert('Veuillez signer électroniquement');
            return;
        }
        onNext();
    };

    return (
        <div className="bg-white p-8 rounded-lg shadow-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Documents et signature</h2>

            <div className="space-y-8">
                {/* Photo de profil */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Photo de profil * <span className="text-red-500">*</span>
                    </h3>
                    {!selfie ? (
                        <div className="border-2 border-dashed border-red-300 rounded-xl p-8 text-center bg-red-50">
                            <Camera className="w-12 h-12 text-red-400 mx-auto mb-4"/>
                            <p className="text-red-600 font-medium mb-6">Photo obligatoire</p>
                            <div className="flex flex-col space-y-3 max-w-xs mx-auto">
                                <button
                                    onClick={startCamera}
                                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                                >
                                    <Camera className="w-4 h-4"/>
                                    <span>Prendre une photo</span>
                                </button>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
                                >
                                    <Upload className="w-4 h-4"/>
                                    <span>Choisir un fichier</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center">
                            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-green-400 mx-auto mb-4">
                                <img src={selfie} alt="Selfie de l'utilisateur" className="w-full h-full object-cover" />
                            </div>
                            <div className="flex items-center justify-center space-x-2 mb-4">
                                <Check className="w-5 h-5 text-green-600" />
                                <span className="text-green-600 font-medium">Photo validée</span>
                            </div>
                            <button
                                onClick={() => onSelfieChange(null)}
                                className="text-gray-600 hover:text-gray-800 underline"
                            >
                                Changer la photo
                            </button>
                        </div>
                    )}

                    {/* Éléments cachés pour la caméra */}
                    <div className="hidden">
                        <video ref={videoRef} className="w-full max-w-sm mx-auto rounded-lg mb-4" />
                        <button onClick={capturePhoto} className="bg-blue-600 text-white px-6 py-3 rounded-lg">
                            Capturer
                        </button>
                    </div>
                    <canvas ref={canvasRef} className="hidden" />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'selfie')}
                        className="hidden"
                    />
                </div>

                {/* Document d'identité (optionnel) */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Pièce d'identité (optionnelle)</h3>
                    {!identityDocument ? (
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <button
                                onClick={() => identityFileInputRef.current?.click()}
                                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                Choisir un fichier
                            </button>
                        </div>
                    ) : (
                        <div className="text-center">
                            <img src={identityDocument} alt="Document" className="max-w-md mx-auto rounded-lg border" />
                            <button
                                onClick={() => onDocumentChange(null)}
                                className="mt-4 text-gray-600 hover:text-gray-800 underline"
                            >
                                Changer le document
                            </button>
                        </div>
                    )}
                    <input
                        ref={identityFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'document')}
                        className="hidden"
                    />
                </div>

                {/* Signature */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Signature * <span className="text-red-500">*</span>
                    </h3>
                    {!signature ? (
                        <div className="border-2 border-dashed border-red-300 rounded-xl p-8 text-center bg-red-50">
                            <PenTool className="w-12 h-12 text-red-400 mx-auto mb-4" />
                            <p className="text-red-600 font-medium mb-6">Signature obligatoire</p>
                            <button
                                onClick={initSignaturePad}
                                className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                            >
                                Signer maintenant
                            </button>
                        </div>
                    ) : (
                        <div className="text-center">
                            <div className="max-w-md mx-auto mb-4 p-4 border-2 border-green-300 rounded-lg bg-green-50">
                                <img src={signature} alt="Signature" className="w-full" />
                            </div>
                            <div className="flex items-center justify-center space-x-2 mb-4">
                                <Check className="w-5 h-5 text-green-600" />
                                <span className="text-green-600 font-medium">Signature validée</span>
                            </div>
                            <button
                                onClick={() => onSignatureChange(null)}
                                className="text-gray-600 hover:text-gray-800 underline"
                            >
                                Refaire la signature
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal signature avec gestion scroll et tactile améliorée */}
            {isSignaturePadOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: 'hidden' // Empêcher le scroll dans le modal
                    }}
                >
                    <div
                        className="bg-white rounded-lg w-full max-w-lg mx-4 overflow-hidden"
                        style={{ maxHeight: '90vh' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header du modal */}
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold">Votre signature</h3>
                                <button
                                    onClick={() => setIsSignaturePadOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                                Signez avec votre doigt ou la souris dans la zone ci-dessous
                            </p>
                        </div>

                        {/* Zone de signature */}
                        <div className="p-6">
                            <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                                <canvas
                                    ref={signatureCanvasRef}
                                    className="w-full cursor-crosshair touch-none"
                                    style={{
                                        height: '200px',
                                        touchAction: 'none' // Important pour mobile
                                    }}
                                    // Événements souris
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    // Événements tactiles
                                    onTouchStart={startDrawing}
                                    onTouchMove={draw}
                                    onTouchEnd={stopDrawing}
                                    onTouchCancel={stopDrawing}
                                />
                            </div>

                            {/* Indication pour mobile */}
                            <p className="text-xs text-gray-500 mt-2 text-center md:hidden">
                                ✋ Utilisez votre doigt pour signer
                            </p>
                            <p className="text-xs text-gray-500 mt-2 text-center hidden md:block">
                                🖱️ Utilisez votre souris pour signer
                            </p>
                        </div>

                        {/* Boutons */}
                        <div className="p-6 border-t border-gray-200">
                            <div className="grid grid-cols-3 gap-3">
                                <button
                                    onClick={clearSignature}
                                    className="bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 transition-colors font-medium text-sm"
                                >
                                    Effacer
                                </button>
                                <button
                                    onClick={() => setIsSignaturePadOpen(false)}
                                    className="bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={saveSignature}
                                    className="bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                                >
                                    Valider
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Boutons navigation */}
            <div className="flex space-x-4 mt-8">
                <button
                    onClick={onPrevious}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                >
                    Retour
                </button>
                <button
                    onClick={handleNext}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
                >
                    Continuer
                </button>
            </div>
        </div>
    );
};

export default DocumentsForm;
